import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Btn } from "@/components/primitives";
import { SignaturePad } from "@/components/SignaturePad";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const inputCls = "w-full h-10 px-3 rounded-md border border-curtron-line bg-curtron-surface text-sm outline-none focus:border-curtron-green focus:ring-2 focus:ring-curtron-green/20 transition";
const labelCls = "text-[11px] uppercase tracking-[0.05em] font-medium text-curtron-sub";

// Fillable agency form. `schema` is the array from /forms; `existing` is the saved form_data (flat).
export default function FormFillDialog({ open, onOpenChange, callId, formKey, label, schema, existing, call, onSaved }) {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  const initial = useMemo(() => {
    const v = {};
    (schema || []).forEach((f) => {
      const saved = existing && existing[f.key];
      if (saved !== undefined && saved !== null) v[f.key] = saved;
      else if (f.prefill && call && call[f.prefill] != null) v[f.key] = call[f.prefill];
      else v[f.key] = f.type === "checkbox" ? false : "";
    });
    return v;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema, existing, call, formKey]);

  useEffect(() => { if (open) setValues(initial); }, [open, initial]);

  const set = (k, val) => setValues((s) => ({ ...s, [k]: val }));

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/calls/${callId}/forms/${formKey}`, values);
      toast.success(`${label} saved`, { description: "PDF added to Documents." });
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="form-fill-dialog">
        <DialogHeader>
          <DialogTitle>{label}</DialogTitle>
          <DialogDescription>Complete this agency form. A PDF is generated and saved to Documents.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {(schema || []).map((f) => (
            <div key={f.key} data-testid={`form-field-${f.key}`}>
              {f.type === "checkbox" ? (
                <label className="flex items-center gap-2 text-sm text-curtron-ink cursor-pointer">
                  <input type="checkbox" checked={!!values[f.key]} onChange={(e) => set(f.key, e.target.checked)}
                    className="w-4 h-4 accent-[#2C4C3B]" data-testid={`form-input-${f.key}`} /> {f.label}
                </label>
              ) : (
                <>
                  <label className={labelCls}>{f.label}</label>
                  {f.type === "textarea" ? (
                    <textarea value={values[f.key] ?? ""} onChange={(e) => set(f.key, e.target.value)} rows={3}
                      className={`${inputCls} mt-1 h-auto py-2`} data-testid={`form-input-${f.key}`} />
                  ) : f.type === "signature" ? (
                    <SignaturePad value={values[f.key]} onChange={(v) => set(f.key, v)} />
                  ) : (
                    <input value={values[f.key] ?? ""} onChange={(e) => set(f.key, e.target.value)}
                      placeholder={f.placeholder || ""} className={`${inputCls} mt-1`} data-testid={`form-input-${f.key}`} />
                  )}
                </>
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Btn variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Btn>
          <Btn onClick={submit} disabled={saving} data-testid="form-fill-save">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save &amp; generate PDF
          </Btn>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
